/** Picker de fecha y hora (calendario + hora) — mismo comportamiento en todos los navegadores. */

import { useEffect, useMemo, useState } from 'react';
import {
  addMinutesToLocalValue,
  buildScheduleLocalValue,
  formatDateTimeLocal,
  parseScheduleLocalParts,
} from '../../utils/dateTimeLocal';
import { D28D_SCHEDULE_TZ, scheduleTzLocalToUtcIso } from '../../utils/d28dScheduleTimezone';

export { formatDateTimeLocal, addMinutesToLocalValue } from '../../utils/dateTimeLocal';

function formatDateTimeDisplay(value) {
  if (!value) return 'Seleccionar fecha y hora';
  try {
    const d = new Date(value.includes('T') && !value.endsWith('Z') ? scheduleTzLocalToUtcIso(value) || value : value);
    if (Number.isNaN(d.getTime())) return String(value);
    return new Intl.DateTimeFormat('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: D28D_SCHEDULE_TZ,
    }).format(d);
  } catch {
    return String(value);
  }
}

export { formatDateTimeDisplay };

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

export default function ScheduleDateTimePicker({
  label,
  value,
  onChange,
  required = false,
  id,
  durationPresets = null,
  onDurationPreset,
  durationPresetsDisabled = false,
}) {
  const parsed = useMemo(() => parseScheduleLocalParts(value), [value]);
  const [date, setDate] = useState(parsed.date);
  const [hour12, setHour12] = useState(parsed.hour12);
  const [minute, setMinute] = useState(parsed.minute);
  const [period, setPeriod] = useState(parsed.period);

  useEffect(() => {
    setDate(parsed.date);
    setHour12(parsed.hour12);
    setMinute(parsed.minute);
    setPeriod(parsed.period);
  }, [parsed.date, parsed.hour12, parsed.minute, parsed.period]);

  const emit = (next) => {
    onChange?.(buildScheduleLocalValue(next));
  };

  const localPreview = buildScheduleLocalValue({ date, hour12, minute, period });

  return (
    <div className="ui-field space-y-2">
      {label ? <span className="ui-field__label">{label}</span> : null}
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,0.6fr))] gap-2">
        <input
          id={id}
          type="date"
          className="ui-input"
          value={date}
          required={required}
          onChange={(e) => {
            const nextDate = e.target.value;
            setDate(nextDate);
            emit({ date: nextDate, hour12, minute, period });
          }}
        />
        <select
          className="ui-input"
          value={hour12}
          aria-label="Hora"
          onChange={(e) => {
            const next = Number(e.target.value);
            setHour12(next);
            emit({ date, hour12: next, minute, period });
          }}
        >
          {HOURS_12.map((h) => (
            <option key={h} value={h}>{h}</option>
          ))}
        </select>
        <select
          className="ui-input"
          value={minute}
          aria-label="Minutos"
          onChange={(e) => {
            const next = Number(e.target.value);
            setMinute(next);
            emit({ date, hour12, minute: next, period });
          }}
        >
          {MINUTES.map((m) => (
            <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
          ))}
        </select>
        <select
          className="ui-input"
          value={period}
          aria-label="a.m. o p.m."
          onChange={(e) => {
            const next = e.target.value;
            setPeriod(next);
            emit({ date, hour12, minute, period: next });
          }}
        >
          <option value="a.m.">a.m.</option>
          <option value="p.m.">p.m.</option>
        </select>
      </div>
      {localPreview ? (
        <p className="ui-field__hint">{formatDateTimeDisplay(localPreview)} · hora Colombia</p>
      ) : (
        <p className="ui-field__hint">Selecciona fecha y hora (zona Colombia / D28D)</p>
      )}
      {durationPresets?.length > 0 && onDurationPreset && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <span className="ui-field__hint">Duración rápida:</span>
          {durationPresets.map((m) => (
            <button
              key={m}
              type="button"
              className="ui-pill"
              disabled={durationPresetsDisabled}
              onClick={() => onDurationPreset(m)}
            >
              {m} min
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
